import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const PortfolioGallery = ({ vendor }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const categories = [
    { id: 'all', label: 'All Work', icon: 'Grid3X3' },
    { id: 'sangeet', label: 'Sangeet', icon: 'Music' },
    { id: 'mehndi', label: 'Mehndi', icon: 'Palette' },
    { id: 'haldi', label: 'Haldi', icon: 'Sun' },
    { id: 'wedding', label: 'Wedding', icon: 'Heart' },
    { id: 'reception', label: 'Reception', icon: 'PartyPopper' },
    { id: 'engagement', label: 'Engagement', icon: 'Ring' }
  ];

  const getFilteredPortfolio = () => {
    if (selectedCategory === 'all') {
      return vendor.portfolio;
    }
    return vendor.portfolio.filter(item => item.category === selectedCategory);
  };

  const handleImageClick = (image, index) => {
    setLightboxImage(image);
    setLightboxIndex(index);
    setShowLightbox(true);
  };

  const handleLightboxClose = () => {
    setShowLightbox(false);
    setLightboxImage(null);
  };

  const handleLightboxPrev = () => {
    const filteredPortfolio = getFilteredPortfolio();
    const newIndex = lightboxIndex === 0 ? filteredPortfolio.length - 1 : lightboxIndex - 1;
    setLightboxIndex(newIndex);
    setLightboxImage(filteredPortfolio[newIndex]);
  };

  const handleLightboxNext = () => {
    const filteredPortfolio = getFilteredPortfolio();
    const newIndex = lightboxIndex === filteredPortfolio.length - 1 ? 0 : lightboxIndex + 1;
    setLightboxIndex(newIndex);
    setLightboxImage(filteredPortfolio[newIndex]);
  };

  const filteredPortfolio = getFilteredPortfolio();

  return (
    <div className="bg-white rounded-xl border border-border shadow-soft">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Portfolio Gallery
          </h3>
          <div className="text-sm text-text-secondary font-caption">
            {filteredPortfolio.length} {selectedCategory === 'all' ? 'total' : selectedCategory} photos
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                selectedCategory === category.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-surface-100 text-text-secondary hover:bg-surface-200 hover:text-primary'
              }`}
            >
              <Icon name={category.icon} size={16} color="currentColor" />
              <span>{category.label}</span>
              <span className="text-xs opacity-75">
                ({vendor.portfolio.filter(item => category.id === 'all' || item.category === category.id).length})
              </span>
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredPortfolio.map((item, index) => (
            <div
              key={item.id}
              className="group relative aspect-square rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-smooth"
              onClick={() => handleImageClick(item, index)}
            >
              <Image
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-smooth flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-smooth">
                  <Icon name="ZoomIn" size={24} color="#FFFFFF" />
                </div>
              </div>
              
              {/* Category Badge */}
              <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
                {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
              </div>
              
              {/* Event Info */}
              {item.eventInfo && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                  <div className="text-white text-sm font-medium">{item.eventInfo.couple}</div>
                  <div className="text-white/80 text-xs font-caption">{item.eventInfo.location}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredPortfolio.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Camera" size={48} color="#8B7B6F" className="mx-auto mb-4" />
            <div className="font-medium text-text-primary mb-2">
              No {selectedCategory} photos yet
            </div>
            <div className="text-sm text-text-secondary font-caption">
              Check back soon for more portfolio updates
            </div>
          </div>
        )}

        {/* Load More */}
        {filteredPortfolio.length > 0 && (
          <div className="mt-6 text-center">
            <Button
              variant="outline"
              size="md"
              iconName="Plus"
              iconSize={16}
            >
              View Full Portfolio
            </Button>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {showLightbox && lightboxImage && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <div className="max-w-4xl w-full max-h-full flex flex-col">
            {/* Lightbox Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="text-white">
                <div className="font-heading font-semibold text-lg">
                  {lightboxImage.title}
                </div>
                <div className="text-sm text-white/80 font-caption">
                  {lightboxImage.category.charAt(0).toUpperCase() + lightboxImage.category.slice(1)} • 
                  {lightboxImage.eventInfo?.couple} • {lightboxImage.eventInfo?.location}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-white/80 text-sm font-data">
                  {lightboxIndex + 1} / {filteredPortfolio.length}
                </span>
                <button
                  onClick={handleLightboxClose}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center transition-smooth"
                >
                  <Icon name="X" size={20} color="#FFFFFF" />
                </button>
              </div>
            </div>

            {/* Lightbox Image */}
            <div className="relative flex-1 flex items-center justify-center">
              <Image
                src={lightboxImage.image}
                alt={lightboxImage.title}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
              
              {/* Navigation Buttons */}
              {filteredPortfolio.length > 1 && (
                <>
                  <button
                    onClick={handleLightboxPrev}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-smooth"
                  >
                    <Icon name="ChevronLeft" size={24} color="#FFFFFF" />
                  </button>
                  <button
                    onClick={handleLightboxNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-smooth"
                  >
                    <Icon name="ChevronRight" size={24} color="#FFFFFF" />
                  </button>
                </>
              )}
            </div>

            {/* Lightbox Footer */}
            <div className="mt-4 flex items-center justify-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                iconSize={16}
                className="text-white hover:bg-white/20"
              >
                Download
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Share"
                iconSize={16}
                className="text-white hover:bg-white/20"
              >
                Share
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Heart"
                iconSize={16}
                className="text-white hover:bg-white/20"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioGallery;